import { useEffect, useState, useCallback, useRef } from "react";
import { useEventStream } from "../context/EventStreamingContext";
import {
  getRemovedUsers,
  getAddedUsers,
} from "../utils/queue";
import {
  removeUserFromQueue
} from "../utils/mutualMatchQueue";
import { db } from "../firebase";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { callFunction } from "../api/callFunction";
import EventAPI from "../api/EventApi";
import Utility from "../utils/utility";
import { showToast } from "../utils/showToast";
import { useNavigate } from "react-router-dom";
import MixerScheduler from "../utils/mixerSchedulerQueue"
import { flattenMatrix, reconstructMatrix } from "../utils/createMatrix";
import { callCloudFunction, getQueueLength, updateEventState, buildGeneratedQueue, buildRegeneratedQueue } from "./queueSectionHelpers";
import { adjustTimeLeftInTransaction } from "./utils/timeLeftAdjust";

export const useQueueSection = () => {
  const navigate = useNavigate();
  const [lateComers, setLateComers] = useState(null);
  const [removedUsers, setRemovedUsers] = useState([]);
  const [pendingRemovedUsers, setPendingRemovedUsers] = useState([]);
  const [newQueue, setNewQueue] = useState(null);
  const { eventData, loading, eventUsersProfile } = useEventStream();
  const [editEnabled, setEditEnabled] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [savingQueue, setSavingQueue] = useState(false);
  const [queueLength, setQueueLength] = useState(
    eventData.queue?.movers?.length || 0
  );
  // Use a ref to track when to play sound
  const lastTimeLeftRef = useRef(null);

  const [event, setEvent] = useState({
    id: eventData?.id || null,
    queue: eventData?.queue || {},
    activeSession: eventData?.activeSession || {},
    currentQueue: eventData?.queue,
    movers: eventData?.movers,
    sitters: eventData?.sitters,
  });

  const formatRoundMetric = useCallback((metric, queueLength, eventData) => {
    const maxRounds = eventData.maxRound;
    const displayMax = (maxRounds !== undefined && typeof maxRounds === 'number' && !isNaN(maxRounds))
      ? Math.min(queueLength, maxRounds)
      : queueLength;
    return `${metric} / ${displayMax}`;
  }, []);

  // Function to play the alert sound (moved before any conditional returns)
  const playAlertSound = useCallback(() => {
    const audio = new Audio('/ship-bell-two-chimes.mp3');
    audio.play()
      .catch(error => console.warn('Error playing sound:', error));
  }, []);

  // Define convertSeconds with useCallback before any conditionals
  const convertSeconds = useCallback((timeLeftInSec) => {
    if (
      timeLeftInSec === undefined ||
      timeLeftInSec === null ||
      !timeLeftInSec
    ) {
      return "";
    }

    const sessionAtTime = event.activeSession?.at;
    if (!sessionAtTime) return "";

    let sessionStartTime;
    try {
      sessionStartTime = Utility.convertDate(sessionAtTime).getTime();
      if (isNaN(sessionStartTime)) {
        console.error("Invalid session start time:", sessionAtTime);
        return "";
      }
    } catch (error) {
      console.error("Error converting date:", error);
      return "";
    }

    const elapsedTime = (Date.now() - sessionStartTime) / 1000;
    const currentTimeLeft = Number(timeLeftInSec) - elapsedTime;

    // Round to nearest second for display
    const roundedTimeLeft = Math.floor(currentTimeLeft);

    // Check if we should play sound - do this outside the formatting function
    if (roundedTimeLeft === 30 && lastTimeLeftRef.current !== 30) {
      playAlertSound();
    } else if (roundedTimeLeft === 10 && lastTimeLeftRef.current !== 10) {
      playAlertSound();
      setTimeout(() => playAlertSound(), 1000); // Play the second sound after 500ms
    }
    // Update our ref to avoid repeating the sound multiple times
    lastTimeLeftRef.current = roundedTimeLeft;

    if (roundedTimeLeft <= 0) {
      return "0s";
    }

    if (roundedTimeLeft < 60) {
      return `${roundedTimeLeft}s`;
    }

    const minutes = Math.floor(roundedTimeLeft / 60);
    const remainingSeconds = roundedTimeLeft % 60;
    return `${minutes}m ${remainingSeconds}s`;
  }, [event.activeSession?.at, playAlertSound]);

  const startSession = async () => {
    // Check if the event type is 1 and the queueMatrix is empty
    if (!eventData.queue) {
      alert("Save the queue before starting the event session.");
      return;
    }
    else if (Object.keys(event.currentQueue.queueMatrix).length === 0) {
      alert("Cannot start the event session: No matches found.");
      return;
    }

    const confirmStart = window.confirm(
      "Do you want to start the event? The event will start in 10 minute."
    );
    if (confirmStart) {
      setButtonLoading(true);

      try {
        await callFunction('createScheduleToUpdateCurrentRound', eventData);
        showToast("Event session started successfully");
      } catch (error) {
        alert("Error starting event session: " + error.message);
      } finally {
        setButtonLoading(false);
      }
    }
  };

  const handleEndEvent = async (action, extraParams = {}) => {
    setButtonLoading(true);
    const params = {
      eventID: eventData?.id,
      taskID: eventData?.activeSession.taskID,
      ...extraParams
    };
    try {
      const res = await callFunction('stopEventSession', params);
      if (!res.error) {
        showToast(`Event session ${action}ed successfully`);
      } else {
        alert(`Error ${action}ing event session: ` + res.message);
      }
    } catch (error) {
      // Without this catch a thrown callable error skipped the loading reset,
      // leaving End Event stuck disabled with no explanation mid-event.
      console.error(`Error ${action}ing event session:`, error);
      showToast(`Failed to ${action} the event session: ${error.message}`, { type: "error" });
    } finally {
      setButtonLoading(false);
    }
  };

  const stopSession = async () => {
    if (window.confirm(`Do you want to Stop and Reset the event?`)) {
      await handleEndEvent('stop');
    }

  };

  const finishEvent = async () => {
    if (window.confirm(`Do you want to Finish and Complete the event?`)) {
      await handleEndEvent('finish', { isCompleted: true });
    }

  };

  // Effect for updating event state and queue length
  useEffect(() => {
    setEvent(updateEventState(eventData));
    setQueueLength(getQueueLength(eventData));
  }, [eventData]);

  // Effect for updating late comers and removed users
  useEffect(() => {
    if (!eventData?.queue) return;

    const { movers, sitters, queue } = eventData;
    const { movers: moversQueue, sitters: sittersQueue } = queue;

    if (eventData.eventType == 0 && eventData.groupCount == 1 && !sittersQueue) return;
    if (eventData.groupCount != 1 && (!moversQueue || !sittersQueue)) return;
    const effectiveMoversQueue = moversQueue || [];

    const lateComersWithRSVPs = {
      movers: [...getAddedUsers(movers, sitters, effectiveMoversQueue, sittersQueue).movers],
      sitters: [...getAddedUsers(movers, sitters, effectiveMoversQueue, sittersQueue).sitters]
    };



    setLateComers(lateComersWithRSVPs);
    setRemovedUsers(getRemovedUsers(movers, sitters, effectiveMoversQueue, sittersQueue));
    setQueueLength(getQueueLength(eventData));
  }, [eventData?.queue, eventData?.movers, eventData?.sitters, eventData?.rsvpMovers, eventData?.rsvpSitters]);

  const [queueUnsaved, setQueueUnsaved] = useState(false);

  // Regenerate queue with late comers.
  const handleRegenerateQueue = async () => {
    const newQueue = await buildRegeneratedQueue(event, eventData);

    setNewQueue(newQueue);
    setQueueUnsaved(true);
  };

  const handleGenerateQueue = async () => {
    const newQueue = await buildGeneratedQueue(event, eventData);

    setQueueUnsaved(true);

    setEvent({
      ...event,
      queue: newQueue,
      currentQueue: newQueue,
    });
  };

  const deleteQueue = async () => {
    if (window.confirm("Are you sure?")) {
      try {
        const docRef = doc(db, "events", event.id);
        await updateDoc(docRef, {
          queue: null,
        });
        // Only leave the page once the delete actually landed — the old
        // fire-and-forget navigated away and hid any failure.
        navigate(`/events/${event.id}/edit`);
      } catch (error) {
        console.error("Error deleting queue:", error);
        showToast("Failed to delete the queue: " + error.message, { type: "error" });
      }
    }
  };

  const onRemoveFromQueue = async (userID) => {
    // Add user to pending removals instead of immediate DB update
    setPendingRemovedUsers(prev => [...new Set([...prev, userID])]);

    // Get the list of all user IDs that should be considered removed (previous DB + current pending)
    const currentRemovedInDB = event.queue?.removedUsers || [];
    const allPotentiallyRemovedUsers = Array.from(new Set([...currentRemovedInDB, ...pendingRemovedUsers, userID]));

    if (eventData.groupCount == 2) {
      // Determine user type (mover or sitter)
      let userType = 'sitter';
      if (eventData.movers?.includes(userID)) {
        userType = 'mover';
      }

      // Use removeUserFromQueue for mutual match queue
      try {
        const newQueueData = await removeUserFromQueue(
          event,
          eventData.activeSession?.currentRound || 0,
          userID,
          userType
        );

        // Update state with the new queue data
        setNewQueue({
          at: serverTimestamp(),
          queueMatrix: newQueueData.queueMatrix,
          consentMatrix: newQueueData.consentMatrix,
          movers: newQueueData.movers,
          sitters: newQueueData.sitters,
          removedMovers: newQueueData.removedMovers,
          removedSitters: newQueueData.removedSitters
        });
        setQueueUnsaved(true);
      } catch (error) {
        console.error("Error removing user from mutual match queue:", error);
        showToast("Failed to remove user from queue", { type: "error" });
      }
    } else {
      // Find the user's index in the original queue sitters array
      // This is crucial for matrix calculations based on the original participant list
      const sitterIndex = event.queue?.sitters?.findIndex(id => id === userID);

      // Handle case where user might not be found in the original sitters list (shouldn't happen ideally)
      if (sitterIndex === -1 && event.queue?.sitters) {
        console.warn(`User ${userID} not found in original queue sitters used for matrix generation.`);
        // Decide if we should proceed or return. For now, let's proceed but log.
      }

      // Indices of *all* potentially removed users based on the *original* sitters list
      const latestRemovedUsersIndices = allPotentiallyRemovedUsers
        .map(removedUserID => event.queue?.sitters?.findIndex(id => id === removedUserID))
        .filter(index => index !== -1); // Filter out any potential -1s if sitters isn't defined yet or user not found

      // Reconstruct the raw matrix used initially for this queue
      const baseQueueMatrixRaw = event.queue?.queueMatrixRaw;
      if (!baseQueueMatrixRaw) {
        console.error("Cannot recalculate queue removal, base queueMatrixRaw is missing.");
        // Potentially show an error to the user or handle gracefully
        return;
      }
      const oldQueueMatrix = reconstructMatrix(baseQueueMatrixRaw);

      // Initialize MixerScheduler with the original matrix dimensions and data
      const initialParticipantCount = oldQueueMatrix[0]?.length || event.queue?.sitters?.length || 0;
      const mixer = new MixerScheduler(initialParticipantCount, oldQueueMatrix);

      const newQueueMatrix = mixer.removeParticipantsFromSchedule(eventData.activeSession?.currentRound || 0, latestRemovedUsersIndices);

      // Flatten and process the result
      const flattenedQueueMatrix = flattenMatrix(newQueueMatrix);
      const queueMatrix = mixer.removeDuplicateMeetings(flattenedQueueMatrix);

      // Update the state with the new matrix and the correct remaining sitters
      // Keep track of DB removed users separate from the total potentially removed for calculation
      setNewQueue({
        at: serverTimestamp(), // Use server timestamp for potential save
        queueMatrix,
        queueMatrixRaw: flattenMatrix(newQueueMatrix), // Use the newly calculated raw matrix
        sitters: event.queue.sitters, // Original sitters list remains the reference
        removedUsers: currentRemovedInDB // Only store DB-persisted removed users here
      });
      setQueueUnsaved(true);
    }
  };

  const saveQueue = async () => {
    setSavingQueue(true);
    const docRef = doc(db, "events", event.id);
    // Use the recalculated newQueue if it exists, otherwise the current event queue
    const queueToSave = newQueue || event.queue;

    if (!queueToSave) {
      console.error("No queue data available to save.");
      showToast("Error: No queue data to save.", { type: "error" });
      setSavingQueue(false);
      return false;
    }

    try {
      // Create a single update object, starting with the base queue structure
      const updateObject = {
        // Ensure queue.at is set appropriately. If newQueue exists, use its 'at', else keep existing.
        // If saving for the first time (newQueue generated but not from removal), 'at' should be serverTimestamp().
        // If saving after removal (newQueue exists), 'at' might be serverTimestamp() from onRemoveFromQueue.
        // If just saving table numbers without queue changes, 'at' shouldn't change? This needs clarification.
        // For now, let's assume queueToSave structure is correct as prepared.
        queue: {
          ...queueToSave,
          // Ensure 'at' is handled correctly - might need adjustment based on context (initial save vs. update)
          // If newQueue exists, its 'at' is already serverTimestamp(). If not, we might not want to update 'at'.
          at: newQueue ? queueToSave.at : event.queue.at // Tentative: Use new 'at' only if newQueue exists
        }
      };

      // If there are pending removals, merge them with existing DB removals for the update
      if (pendingRemovedUsers.length > 0) {
        if (eventData.groupCount == 2) {
          // For groupCount == 2, determine if each pending user is a mover or sitter
          const existingRemovedMovers = queueToSave.removedMovers || [];
          const existingRemovedSitters = queueToSave.removedSitters || [];
          const newRemovedMovers = [];
          const newRemovedSitters = [];

          pendingRemovedUsers.forEach(userID => {
            if (eventData.movers?.includes(userID)) {
              newRemovedMovers.push(userID);
            } else if (eventData.sitters?.includes(userID)) {
              newRemovedSitters.push(userID);
            }
          });

          // Merge with existing and ensure uniqueness
          const finalRemovedMovers = Array.from(new Set([...existingRemovedMovers, ...newRemovedMovers]));
          const finalRemovedSitters = Array.from(new Set([...existingRemovedSitters, ...newRemovedSitters]));

          // Merged arrays go into the nested queue map ONLY. Adding dotted
          // 'queue.removedMovers' paths alongside the top-level 'queue' field makes
          // Firestore reject the whole update (field + sub-field conflict), which
          // silently dropped every mid-event removal (GIS-165).
          updateObject.queue.removedMovers = finalRemovedMovers;
          updateObject.queue.removedSitters = finalRemovedSitters;
        } else {
          const existingRemoved = queueToSave.removedUsers || [];
          // Use Set to ensure uniqueness before saving
          const finalRemovedUsers = Array.from(new Set([...existingRemoved, ...pendingRemovedUsers]));
          // Nested map only — a dotted 'queue.removedUsers' path next to the
          // top-level 'queue' field makes Firestore reject the whole update (GIS-165).
          updateObject.queue.removedUsers = finalRemovedUsers;
        }
      }


      // Add table numbers to the update object using dot notation
      queueToSave.sitters?.forEach((userID, index) => {
        let userNumber = index + 1;
        const userNumberLabel = eventData.groupCount == 1 ? `badgeNumber` : 'table';
        // This logic seems specific to groupCount == 1, might need adjustment if table numbers depend on removals etc.
        // if (eventData.groupCount === 1) {
        //   userNumber = index + 1; // This seems redundant with the initial assignment
        // }
        updateObject[`eventUsers.${userID}.${userNumberLabel}`] = userNumber;
      });


      // Single update call
      await updateDoc(docRef, updateObject);

      // Update state: clear newQueue, reset unsaved flag, clear pending removals
      if (newQueue) setNewQueue(null);
      setQueueUnsaved(false);
      setPendingRemovedUsers([]); // Clear pending removals after successful save


      showToast("Queue saved successfully!", { type: "success" });
      setSavingQueue(false);
      return true;
    } catch (error) {
      console.error('Error updating queue and tables:', error);
      showToast("Failed to save queue: " + error.message, { type: "error" });
      // Do not clear pending removals if save failed
      setSavingQueue(false);
      throw new Error('Failed to update queue and tables');
    }
  };

  const showStartButton = (() => {
    // First condition
    if (!eventData.queue) {
      return false;
    }

    // Second condition with null checks
    if (!event.currentQueue || !event.currentQueue.queueMatrix ||
      Object.keys(event.currentQueue.queueMatrix).length === 0) {
      return false;
    }

    // Final condition
    return event.currentQueue && !event.activeSession?.taskID;
  })();

  const hasNewConsents = (() => {
    if (!eventData.eventUsers?.lastUpdated || !eventData.queue?.at) {
      return false
    }

    // Convert to Date objects based on their type
    const lastUpdatedDate = eventData.eventUsers.lastUpdated.toDate
      ? eventData.eventUsers.lastUpdated.toDate()
      : new Date(eventData.eventUsers.lastUpdated);

    const queueAtDate = eventData.queue.at.toDate
      ? eventData.queue.at.toDate()
      : new Date(eventData.queue.at);

    // Validate the dates
    if (isNaN(lastUpdatedDate) || isNaN(queueAtDate)) {
      throw new Error("One of the dates is invalid");
    }

    // Compare the dates
    if (lastUpdatedDate > queueAtDate) {
      return true;
    } else if (lastUpdatedDate < queueAtDate) {
      return false;
    } else {
      return false;
    }
  })()

  const onUpdateMetric = async (
    field,
    oldValue,
    newValue,
    cancelExistingTask = false
  ) => {
    if (!Number.isNaN(newValue) && Number(newValue) > 0) {
      newValue = Number(newValue);
      if (oldValue === newValue) return;
    } else {
      return;
    }
    setNewQueue(null);
    // Callers (adjustRound/adjustTimeLeft) don't await this, so every failure
    // has to be surfaced here or it is an invisible unhandled rejection.
    try {
      if (field === "timeLeft") {
        // GIS-279. A delta applied in a transaction, never an absolute write.
        // Paid and free extends increment timeLeft on their own, and writing
        // the number this screen last saw would erase one that landed seconds
        // earlier (a paid extend charged with no minutes added).
        const result = await adjustTimeLeftInTransaction(eventData.id, {
          seenTimeLeft: oldValue,
          newTimeLeft: newValue,
          seenRound: event.activeSession?.currentRound,
        });
        if (result?.status !== "updated") {
          const skipped = {
            missing: "Couldn't find this event. Refresh the page and try again.",
            round_changed: "The round changed before the time was saved. Check the clock and adjust again.",
            not_positive: "That would leave no time on the clock, so the time was not changed.",
          };
          showToast(skipped[result?.status] || "The time was not changed. Try again.", { type: "error" });
          return;
        }
      } else {
        await EventAPI.updateEvent(eventData.id, {
          [`activeSession.${field}`]: newValue,
        });
      }
    } catch (error) {
      console.error(`Failed to update activeSession.${field}:`, error);
      showToast("Failed to update the session", { type: "error" });
      return;
    }

    if (cancelExistingTask) {
      const params = {
        eventID: eventData?.id,
        cancelExistingTask,
      };

      try {
        await callCloudFunction("updateCurrentRound", params);
      } catch (error) {
        // The new time is saved, but the round task still fires on the OLD
        // schedule — silent failure here means rounds advance at the wrong
        // moment mid-event, so say it out loud.
        console.error("updateCurrentRound reschedule failed:", error);
        showToast("Time saved, but the round timer was not rescheduled. Adjust it again to retry.", { type: "error" });
      }
    }
  };

  const increaseRound = (metric) => {
    adjustRound(metric + 1);
  };
  const increaseTimeLeft = (metric) => {
    adjustTimeLeft(metric + 60);
  };

  const decreaseRound = (metric) => {
    adjustRound(metric - 1);
  };
  const decreaseTimeLeft = (metric) => {
    adjustTimeLeft(metric - 60);
  };

  const adjustTimeLeft = (newValue) => {
    onUpdateMetric("timeLeft", event.activeSession?.timeLeft, newValue, true);
    setEditEnabled(false);
  };


  const adjustRound = (newValue) => {
    onUpdateMetric("currentRound", event.activeSession?.currentRound, newValue);
    setEditEnabled(false);
  };

  return {
    eventData,
    loading,
    eventUsersProfile,
    lateComers,
    removedUsers,
    pendingRemovedUsers,
    newQueue,
    editEnabled,
    setEditEnabled,
    buttonLoading,
    savingQueue,
    queueLength,
    event,
    formatRoundMetric,
    convertSeconds,
    startSession,
    stopSession,
    finishEvent,
    handleRegenerateQueue,
    handleGenerateQueue,
    deleteQueue,
    onRemoveFromQueue,
    saveQueue,
    queueUnsaved,
    showStartButton,
    hasNewConsents,
    increaseRound,
    decreaseRound,
    increaseTimeLeft,
    decreaseTimeLeft,
  };
};
